import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { db } from "../firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  and,
  or,
} from "firebase/firestore";

const RecoveryTrackerContext = createContext();

export const RecoveryTrackerProvider = ({ children }) => {
  const [showRecoveryTracker, setShowRecoveryTracker] = useState(false);
  const [accessOptions, setAccessOptions] = useState([]);
  const [currentAccess, setCurrentAccess] = useState([]);
  const selectedUserId = sessionStorage.getItem("userId");

  const fetchCenterOptions = useCallback(async (centerId) => {
    const centersQuery = query(
      collection(db, "users"),
      and(
        where("center_id", "==", centerId),
        where("user_role", "==", "center")
      )
    );

    const centersSnapshot = await getDocs(centersQuery);
    let centerOptions = [];

    if (!centersSnapshot.empty) {
      const centerUserDoc = centersSnapshot.docs[0];
      const centerData = centerUserDoc.data();
      const centerOrgDoc = await getDoc(
        doc(db, "centers", centerData.center_id)
      );

      if (centerOrgDoc.exists()) {
        const centerOrgData = centerOrgDoc.data();
        centerOptions = [
          {
            id: centerUserDoc.id,
            name: centerOrgData.name_of_hub,
            type: "center",
          },
        ];
      }
    }
    return centerOptions;
  }, []);

  const fetchPartnerOptions = useCallback(async (centerId) => {
    const partnersQuery = query(
      collection(db, "users"),
      and(
        or(
          where("center_id", "array-contains", centerId),
          where("center_id", "==", centerId)
        ),
        where("user_role", "==", "partner")
      )
    );

    const partnersSnapshot = await getDocs(partnersQuery);
    const partnerOptions = await Promise.all(
      partnersSnapshot.docs.map(async (document) => {
        const userData = document.data();
        if (!userData.partner_id) return null;

        const partnerDoc = await getDoc(
          doc(db, "partners", userData.partner_id)
        );
        if (partnerDoc.exists()) {
          const partnerData = partnerDoc.data();
          return {
            id: document.id,
            name: partnerData.name_of_org,
            type: "partner",
          };
        }
        return null;
      })
    );
    return partnerOptions.filter(Boolean);
  }, []);

  const fetchAccessData = useCallback(async () => {
    if (!selectedUserId) return;

    try {
      const userDoc = await getDoc(doc(db, "users", selectedUserId));
      if (userDoc.exists()) {
        let centerId = userDoc.data().center_id;
        if (centerId === "") {
          centerId = "1234";
        }

        const [centerOptions, partnerOptions] = await Promise.all([
          fetchCenterOptions(centerId),
          fetchPartnerOptions(centerId),
        ]);

        const allOptions = [...centerOptions, ...partnerOptions];
        setAccessOptions(allOptions);
        setCurrentAccess(userDoc.data()?.has_access_to_rt || []);
      }
    } catch (error) {
      console.error("Error fetching access data:", error);
    }
  }, [selectedUserId, fetchCenterOptions, fetchPartnerOptions]);

  // Initial fetch
  useEffect(() => {
    if (selectedUserId) {
      fetchAccessData();
    }
  }, [selectedUserId, fetchAccessData]);

  const value = useMemo(
    () => ({
      showRecoveryTracker,
      setShowRecoveryTracker,
      accessOptions,
      currentAccess,
      fetchAccessData,
    }),
    [showRecoveryTracker, accessOptions, currentAccess, fetchAccessData]
  );

  return (
    <RecoveryTrackerContext.Provider value={value}>
      {children}
    </RecoveryTrackerContext.Provider>
  );
};

export const useRecoveryTracker = () => {
  const context = useContext(RecoveryTrackerContext);
  if (context === undefined) {
    throw new Error(
      "useRecoveryTracker must be used within a RecoveryTrackerProvider"
    );
  }
  return context;
};
